import { Button, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { ReactComponent as RemoveIcon } from "@king-ict/eupisi-ui/components/Icon/RemoveIcon.svg";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { Module } from "../../models/ProgramRequest/Module";
import styles from "./ProgramRequestEdit.module.scss";

export interface ModuleInfoProps {
  name: string;
  modules: Module[];
  qualificationType: number;
  getErrorForPath: (path: string) => string | undefined;
  onChange: (e: any) => void;
}

export function ModuleInfo({
  name,
  modules,
  qualificationType,
  getErrorForPath,
  onChange,
}: ModuleInfoProps) {
  const { t } = useTranslation();

  const cloneModules = () => modules.map((m) => ({ ...m, learningSets: (m.learningSets ?? []).map((ls) => ({ ...ls })) }));

  const handleModuleAdd = (event: React.MouseEvent<any, MouseEvent>) => {
    const newList = cloneModules();
    newList.push({ name: "", learningSets: [{ name: "", code: "", csvet: 1, level: 2 }] });
    onChange({
      ...event,
      target: {
        ...event.target,
        name,
        value: newList,
      }
    });
  };

  const handleModuleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newList = cloneModules();
    newList[index].name = event.target.value;
    onChange({
      ...event,
      target: {
        ...event.target,
        name,
        value: newList,
      }
    });
  };

  const handleModuleRemove = (event: React.MouseEvent<any, MouseEvent>, index: number) => {
    const newList = cloneModules();
    newList.splice(index, 1);
    onChange({
      ...event,
      target: {
        ...event.target,
        name,
        value: newList,
      }
    });
  };

  const handleLearningSetAdd = (event: React.MouseEvent<any, MouseEvent>, index: number) => {
    const newList = cloneModules();
    newList[index].learningSets.push({ name: "", code: "", csvet: 1, level: 2 });
    onChange({
      ...event,
      target: {
        ...event.target,
        name,
        value: newList,
      }
    });
  };

  const handleLearningSetNameChange = (event: React.ChangeEvent<HTMLInputElement>, modIndex: number, lsIndex: number) => {
    const newList = cloneModules();
    newList[modIndex].learningSets[lsIndex].name = event.target.value;
    onChange({
      ...event,
      target: {
        ...event.target,
        name,
        value: newList,
      }
    });
  };

  const handleLearningSetCodeChange = (event: React.ChangeEvent<HTMLInputElement>, modIndex: number, lsIndex: number) => {
    const newList = cloneModules();
    newList[modIndex].learningSets[lsIndex].code = event.target.value;
    onChange({
      ...event,
      target: {
        ...event.target,
        name,
        value: newList,
      }
    });
  };

  const handleLearningSetLevelChange = (event: React.ChangeEvent<HTMLInputElement>, modIndex: number, lsIndex: number) => {
    const newList = cloneModules();
    newList[modIndex].learningSets[lsIndex].level = parseInt(event.target.value, 10);
    onChange({
      ...event,
      target: {
        ...event.target,
        name,
        value: newList,
      }
    });
  };

  const handleLearningSetcSVETChange = (event: React.ChangeEvent<HTMLInputElement>, modIndex: number, lsIndex: number) => {
    const newList = cloneModules();
    newList[modIndex].learningSets[lsIndex].csvet = parseInt(event.target.value, 10);
    onChange({
      ...event,
      target: {
        ...event.target,
        name,
        value: newList,
      }
    });
  };

  const handleLearningSetRemove = (event: React.MouseEvent<any, MouseEvent>, modIndex: number, lsIndex: number) => {
    const newList = cloneModules();
    newList[modIndex].learningSets.splice(lsIndex, 1);
    onChange({
      ...event,
      target: {
        ...event.target,
        name,
        value: newList,
      }
    });
  };

  return (
    <>
      <Row>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("request.modules")}
          </Typography>
        </Col>
      </Row>

      {modules.map((module, modIndex) => (
        <Row key={`module-${modIndex}`} className={modIndex !== 0 ? "mt-3" : ""}>
          <Col md={12}>
            <div className={styles.card}>
              <Row>
                <Col md={12}>
                  <TextField
                    label={t("request.moduleName")}
                    value={module.name}
                    maxLength={255}
                    onChange={(e) => handleModuleChange(e, modIndex)}
                    errorText={getErrorForPath(`modules[${modIndex}].name`)}
                  />
                </Col>
              </Row>
              {module.learningSets && module.learningSets.map((learningSet, lsIndex) => (
                <Row key={`module-${modIndex}-learning-set-${lsIndex}`}>
                  <Col md={12}>
                    <Row>
                      <Col md={11}>
                        <TextField
                          label={t("request.moduleLearningSetName")}
                          value={learningSet.name}
                          maxLength={255}
                          onChange={(e) => handleLearningSetNameChange(e, modIndex, lsIndex)}
                          errorText={getErrorForPath(`modules[${modIndex}].learningSets[${lsIndex}].name`)}
                        />                        
                      </Col>
                      <Col md={1} className="mt-4">
                        <Button
                          className={`p-0 ${lsIndex > 0 ? "visible" : "invisible"}`}
                          boxless
                          disabled={modules[modIndex].learningSets.length <= 1}
                          onClick={(e) => handleLearningSetRemove(e, modIndex, lsIndex)}
                        >
                          <RemoveIcon />
                        </Button>
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={t("request.moduleLearningSetCode")}
                          value={learningSet.code}
                          onChange={(e) => handleLearningSetCodeChange(e, modIndex, lsIndex)}
                          errorText={getErrorForPath(`modules[${modIndex}].learningSets[${lsIndex}].code`)}
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={t("request.moduleLearningSetLevel")}
                          value={learningSet.level}
                          type="number"
                          min={2}
                          max={6}
                          onChange={(e) => handleLearningSetLevelChange(e, modIndex, lsIndex)}
                          errorText={getErrorForPath(`modules[${modIndex}].learningSets[${lsIndex}].level`)}
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={t("request.moduleLearningSetCSVET")}
                          value={learningSet.csvet}
                          type="number"
                          min={1}
                          onChange={(e) => handleLearningSetcSVETChange(e, modIndex, lsIndex)}
                          errorText={getErrorForPath(`modules[${modIndex}].learningSets[${lsIndex}].csvet`)}
                        />
                      </Col>
                    </Row>
                  </Col>

                </Row>
              ))}
              <Row className={"text-right"}>
                <Col md={12}>
                  <Button
                    outline
                    icon={<AddIcon />}
                    onClick={(e) => handleLearningSetAdd(e, modIndex)}
                  >
                    {t("request.moduleLearningSetAdd")}
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col md={12} className="text-right">
                  <Button
                    className={`p-0 ${modIndex > 0 ? 'visible' : 'invisible'}`}
                    boxless
                    disabled={modules.length <= 1}
                    onClick={(e) =>
                      handleModuleRemove(e, modIndex)
                    }
                  >
                    {t("request.moduleRemove")}
                  </Button>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      ))}
      <Row className={"text-right mt-3"}>
        <Col md={12}>
          <Button
            outline
            icon={<AddIcon />}
            onClick={handleModuleAdd}
          >
            {t("request.moduleAdd")}
          </Button>
        </Col>
      </Row>
    </>
  );
}